<script setup lang="ts">
import { provideUseId } from '@headlessui/vue'

provideUseId(() => useId())
const toLocalePath = useLocalePath()

const props = defineProps({
  user: {
    type: Object as PropType<ReturnType<typeof useAuth>['data']['value']>,
    default: null,
    required: false
  }
})

// const lmsDomain = useRuntimeConfig().public.lmsDomain
// const jobAdminDomain = useRuntimeConfig().public.jobAdminDomain

// let settingUrl = `${lmsDomain}/classroom?tab=my-settings&subTab=0`
// if (isJobAdmin(props.user) || isCompanyStaff(props.user)) {
//   settingUrl = `${jobAdminDomain}/profile?tab=setting`
// }

// const profileUrl = `${lmsDomain}/classroom?tab=user-profile`

function handleLogout() {
  // close?: () => void
  //   if (close) close()
  navigateTo(toLocalePath('/logout'))
}
</script>

<template>
  <div class="avatar-dropdown">
    <div class="avatar-dropdown-avatar">
      <div class="avatar-dropdown-avatar-profile">
        <PageForumAvatarInitials
          :image="user?.profile_image?.presigned_get_object_url || ''"
          :first-name="user?.first_name || ''"
          :last-name="user?.last_name || ''"
        />
      </div>

      <CommonIconChevron />
    </div>

    <div class="avatar-dropdown-links">
      <div class="avatar-dropdown-links-avatar">
        <div class="avatar-dropdown-links-avatar-wrapper">
          <PageForumAvatarInitials
            :image="user?.profile_image?.presigned_get_object_url || ''"
            :first-name="user?.first_name || ''"
            :last-name="user?.last_name || ''"
          />
        </div>

        <span v-if="props.user">{{ props.user.first_name }} {{ props.user.last_name }} </span>
      </div>

      <div v-if="props.user" class="avatar-dropdown-links-list">
        <NuxtLink v-if="isStudent(props.user)" to="/profile">
          <CommonIconNavbarProfile />
          <span> Profile </span>

          <CommonSmallNavbarChevron />
        </NuxtLink>

        <NuxtLink to="/settings">
          <CommonIconNavbarSettingsOutline />
          <span> Settings </span>

          <CommonSmallNavbarChevron />
        </NuxtLink>
      </div>

      <div class="avatar-dropdown-links-logout">
        <div class="avatar-dropdown-links-logout-item">
          <a tabindex="0" as="button" :custom="true" href="/" @click.prevent.stop="handleLogout">
            <CommonIconNavbarLogout />

            Logout
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.avatar-dropdown {
  position: relative;
  display: inline-block;
  user-select: none;
  cursor: pointer;

  &-avatar {
    padding: 10px 0;
    display: flex;
    align-items: center;
    gap: 3px;

    svg {
      $size: 12px;
      height: $size;
      width: $size;
      transform: rotate(90deg);
      fill: var(--purple);
      margin-left: 7px;
      transition: transform 0.3s ease-out;
    }

    &-profile {
      height: 35px;
      width: 35px;
      position: relative;
    }

    &-profile::before {
      $margin: -3px;
      content: '';
      position: absolute;
      top: $margin;
      bottom: $margin;
      right: $margin;
      left: $margin;
      border: 1px solid var(--purple);
      border-radius: 50%;
    }
  }

  &-links {
    display: none;
    position: absolute;
    top: 45px;
    right: -20px;
    width: fit-content;
    min-width: 250px;
    transform-origin: top right;
    background: white;
    border: 1px solid gainsboro;
    border-radius: 20px;
    padding: 10px 10px 15px;
    color: var(--darkBlue);
    font-size: 0.95rem;
    box-shadow: 0 5px 20px 10px rgba(0, 0, 0, 0.1);

    &-avatar {
      $avatarSize: 25px;

      display: grid;
      grid-template-columns: $avatarSize 1fr;
      align-items: center;
      padding: 15px 10px 7px;
      gap: 13px;

      &-wrapper {
        height: $avatarSize;
        width: $avatarSize;

        .forum-avatar {
          :deep(.forum-avatar-initials) {
            font-size: 0.6rem;
          }
        }
      }

      span {
        font-size: 0.9rem;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        max-width: 150px;
        color: var(--textMedium);
      }
    }

    &-list {
      margin: 15px 0 0;
      padding: 10px 0;
      display: flex;
      flex-direction: column;
      border-top: 1px solid gainsboro;

      a {
        padding: 15px 10px;
        border-radius: 7px;
        display: flex;
        align-items: center;

        svg {
          $size: 15px;
          height: $size;
          width: $size;
          margin-right: 12px;
          fill: var(--darkBlue);
        }
      }
    }

    &-logout {
      padding: 12px 0 5px;
      border-top: 1px solid gainsboro;
      font-weight: 500;

      a {
        color: var(--purple);
        padding: 15px 10px;
        display: flex;
        align-items: center;
        border-radius: 7px;
      }

      svg {
        $logoutSize: 15px;
        height: $logoutSize;
        width: $logoutSize;
        margin-right: 13px;
        fill: var(--purple);
      }
    }
  }
}

.avatar-dropdown:hover {
  .avatar-dropdown {
    &-avatar {
      svg {
        transform: rotate(270deg);
      }
    }

    &-links {
      display: block;
    }
  }
}

@media (hover: hover) {
  .avatar-dropdown {
    &-links {
      &-logout,
      &-list {
        a:hover {
          background-color: var(--bgMedium);
        }
      }
    }
  }
}

@media (max-width: 1000px) {
  .avatar-dropdown-links {
    &-logout {
      border: none;
    }
  }
}

@media (max-width: 900px) {
  .avatar-dropdown {
    .hide-mobile {
      display: none;
    }
  }
}
</style>
